import type { ElementPlacementMap, ElementGroup } from '../../types';
import type { BaseAdoptableBuyerElementGroup } from '../../sharedBases/adoptableBuyer';
import type { BaseAdoptableSellerElementGroup } from '../../sharedBases/adoptableSeller';

export enum StandardDeviationElementPlacement {
  /**
   * Where all the action happens. The bar directly under the deviation
   */
  PrimaryBar = 'primary-bar',

  /**
   * For now, a purely mobile placement. Below the primary bar.
   */
  SecondaryBar = 'secondary-bar',

  /**
   * Collapsed into the primary bar's more actions menu
   */
  PrimaryBarMenu = 'primary-bar-menu',

  /**
   * Collapsed into the secondary bar's more actions menu
   */
  SecondaryBarMenu = 'secondary-bar-menu',

  /**
   * Completely excluded. Nothing. Nowhere.
   */
  NotIncluded = 'not-included',
}

export const NOT_INCLUDED_VISIBILITY_GROUP: ElementGroup<StandardDeviationElementPlacement> =
  {
    groupPlacement: StandardDeviationElementPlacement.NotIncluded,
    countOnBar: 0,
  };

/**
 * Some elements are only shown on the primary action bar. No collapsing into the more menu or moving about for these guys.
 */
type StandardDeviationElementPlacementPrimaryBarOnly =
  | StandardDeviationElementPlacement.PrimaryBar
  | StandardDeviationElementPlacement.NotIncluded;

/**
 * Some elements are only shown in the primary actionbar's menu. No major publicity for these guys.
 */
type StandardDeviationElementPlacementPrimaryBarMenuOnly =
  | StandardDeviationElementPlacement.PrimaryBarMenu
  | StandardDeviationElementPlacement.NotIncluded;

/**
 * Some elements are shown on the primary action bar, but have the option to drop down to a secondary action bar.
 * No collapsing into the more menu for these guys.
 */
export type StandardDeviationElementPlacementPrimaryOrSecondaryBarOnly =
  | StandardDeviationElementPlacement.PrimaryBar
  | StandardDeviationElementPlacement.SecondaryBar
  | StandardDeviationElementPlacement.NotIncluded;

/**
 * Some elements transcend time and space to appear in two places at once. Our journal deviations'
 * primary and secondary action bar, mostly.
 */
export type StandardDeviationElementPlacementMultipleAtOnce =
  | StandardDeviationElementPlacement.NotIncluded
  | Exclude<
      StandardDeviationElementPlacement,
      StandardDeviationElementPlacement.NotIncluded
    >[];

/**
 * @see BaseAdoptableBuyerElementGroup
 */
export type AdoptableBuyerElementGroup = BaseAdoptableBuyerElementGroup<
  StandardDeviationElementPlacement,
  StandardDeviationElementPlacementPrimaryOrSecondaryBarOnly
>;

/**
 * @see BaseAdoptableSellerElementGroup
 */
export type AdoptableSellerElementGroup = BaseAdoptableSellerElementGroup<
  StandardDeviationElementPlacement,
  StandardDeviationElementPlacementPrimaryOrSecondaryBarOnly
>;

/**
 * All possible elements of our standard actionbar, each mapped to their {@link StandardDeviationElementPlacement}.
 * (e.g. is it on the bar? collapsed into the more menu? completely absent?)
 */
export type StandardDeviationElementPlacementMap = ElementPlacementMap<
  StandardDeviationElementPlacement,
  {
    subscribe: StandardDeviationElementPlacementPrimaryBarOnly;
    comment: StandardDeviationElementPlacementMultipleAtOnce;
    premiumDownload: StandardDeviationElementPlacement;
    freeDownload: StandardDeviationElementPlacement;
    addToGroup: StandardDeviationElementPlacementMultipleAtOnce;
    awardBadge: StandardDeviationElementPlacement;
    favourite: StandardDeviationElementPlacementMultipleAtOnce;
    privateCollection: StandardDeviationElementPlacement;
    boostDeviation: StandardDeviationElementPlacement;
    sellDeviation: StandardDeviationElementPlacementPrimaryOrSecondaryBarOnly;
    editBackground: StandardDeviationElementPlacement;
    deleteDeviation: StandardDeviationElementPlacement;
    editDeviation: StandardDeviationElementPlacement;
    commissionStatus: StandardDeviationElementPlacementPrimaryBarOnly;
    orderCommission: StandardDeviationElementPlacement;
    share: StandardDeviationElementPlacementMultipleAtOnce;
    report: StandardDeviationElementPlacementMultipleAtOnce;
    fullscreen: StandardDeviationElementPlacementPrimaryBarOnly;
    viewOrderHistory: StandardDeviationElementPlacement;
    unpublish: StandardDeviationElementPlacementPrimaryBarMenuOnly;
    draw: StandardDeviationElementPlacementPrimaryBarMenuOnly;
    requestOriginal: StandardDeviationElementPlacementPrimaryBarOnly;
  },
  {
    adoptableBuyer: AdoptableBuyerElementGroup;
    adoptableSeller: AdoptableSellerElementGroup;
  },
  {
    /**
     * Whether there is anything in the map with placement set to {@link StandardDeviationElementPlacement.PrimaryBarMenu}.
     *
     * We could force the consumer to loop through the map and figure it out themselves, but we need to know this in multiple places.
     * Since this data is already present inside the {@link StandardDeviationElementPlacementMap}, it feels squarely within its domain of responsibility.
     */
    hasPrimaryMoreActionsMenu: boolean;

    /**
     * Whether there is anything in the map with placement set to {@link StandardDeviationElementPlacement.SecondaryBarMenu}.
     *
     * We could force the consumer to loop through the map and figure it out themselves, but we need to know this in multiple places.
     * Since this data is already present inside the {@link StandardDeviationElementPlacementMap}, it feels squarely within its domain of responsibility.
     */
    hasSecondaryMoreActionsMenu: boolean;
  }
>;
